import { motion } from "framer-motion";
import {
  GraduationCap,
  Briefcase,
  Award,
  Quote,
  CheckCircle2,
} from "lucide-react";
import { EngineeringJob } from "@/components/site/DecorativeBG";

const CLIENTS = [
  "ZF Friedrichshafen AG",
  "Hyosung T&D",
  "Toshiba T&D",
  "Hitachi Energy",
  "Lauretz and Knudsen",
  "Crompton Greaves",
  "POWERGRID",
  "Ferrabyrne UK · CAB · Dellner",
];

const EXPERTISE = [
  "Heat Treatment",
  "Gravity Die Casting",
  "Rubber Molding",
  "Forging",
  "Machining",
  "Welding",
];

const CERTIFICATIONS = [
  "Lean Six Sigma Black Belt",
  "ISO 9001:2015 Auditor",
  "IATF 16949 Auditor",
  "VDA 6.3 Aligned",
];

export default function Leadership() {
  return (
    <section
      id="leadership"
      data-testid="leadership-section"
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      <EngineeringJob className="-bottom-6 -right-8 lg:right-8" opacity={0.18} bounce />

      <div className="container-x relative">
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="divider-rule" />
            <span className="eyebrow">Leadership</span>
          </div>
          <h2 className="h-section">
            25+ years of building
            <br />
            <span className="text-brand-blue">global supply trust.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left: Premium navy profile card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div
              data-testid="ceo-card"
              className="relative bg-brand-navy text-white p-8 lg:p-10 overflow-hidden shadow-[0_20px_60px_rgba(11,46,89,0.25)]"
            >
              {/* corner accents */}
              <div className="absolute top-0 right-0 h-24 w-24 border-t-2 border-r-2 border-brand-orange/70" />
              <div className="absolute bottom-0 left-0 h-24 w-24 border-b-2 border-l-2 border-brand-orange/70" />

              <div className="relative flex justify-center pt-2">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full border border-white/20" />
                  <div className="absolute -inset-6 rounded-full border border-white/10" />
                  <div className="h-56 w-56 rounded-full overflow-hidden bg-white ring-4 ring-white/15">
                    <img
                      src="/ceo-sachiien-more.jpg"
                      alt="Mr. Sachiien More — Founder & CEO"
                      className="h-full w-full object-cover"
                      style={{ objectPosition: "60% 30%" }}
                    />
                  </div>
                </div>
              </div>

              <div className="relative mt-10">
                <h3 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight leading-tight">
                  Mr. Sachiien More
                </h3>
                <div className="mt-2 text-brand-orange font-semibold tracking-wide">
                  Founder &amp; Business Head
                </div>
              </div>

              <div className="relative h-px bg-white/20 my-8" />

              <div className="relative space-y-5">
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 flex items-center justify-center bg-white/10 border border-white/15 shrink-0">
                    <GraduationCap className="h-5 w-5 text-brand-orange" />
                  </div>
                  <span className="text-white/90 font-medium">
                    Mechanical Engineer
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 flex items-center justify-center bg-white/10 border border-white/15 shrink-0">
                    <Briefcase className="h-5 w-5 text-brand-orange" />
                  </div>
                  <span className="text-white/90 font-medium">
                    25+ years Industry Experience
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 flex items-center justify-center bg-white/10 border border-white/15 shrink-0">
                    <Award className="h-5 w-5 text-brand-orange" />
                  </div>
                  <span className="text-white/90 font-medium">
                    Lean Six Sigma Black Belt &amp; ISO / IATF Auditor
                  </span>
                </div>
              </div>

              <div className="relative mt-9 flex items-center gap-2">
                {["#52B54B", "#1E88E5", "#F5A623"].map((c) => (
                  <span
                    key={c}
                    className="h-1 w-8 inline-block"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: detailed bio */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="relative">
              <div className="hidden lg:flex h-8 w-8 items-center justify-center bg-brand-orange text-white absolute -left-12 top-1">
                <Quote className="h-4 w-4" />
              </div>
              <p className="text-brand-text/80 text-lg lg:text-xl leading-relaxed font-medium">
                A Mechanical Engineer with{" "}
                <span className="text-brand-navy font-bold">
                  over 25 years of rich industry experience
                </span>{" "}
                across automotive and non-automotive sectors — bringing deep
                expertise in Production, Process Engineering, Product
                Development, and Operations Management.
              </p>
            </div>

            <p className="mt-6 text-brand-text/75 leading-relaxed">
              Extensive hands-on knowledge in multiple manufacturing
              technologies —{" "}
              <span className="font-semibold text-brand-navy">
                Heat Treatment, Gravity Die Casting, Rubber Molding, Forging,
                Machining, and Welding
              </span>
              . A certified{" "}
              <span className="font-semibold text-brand-navy">
                Lean Six Sigma Black Belt
              </span>{" "}
              and{" "}
              <span className="font-semibold text-brand-navy">
                ISO 9001:2015 &amp; IATF 16949 Auditor
              </span>
              .
            </p>

            <p className="mt-5 text-brand-text/75 leading-relaxed">
              He leads the organisation with a strong vision for{" "}
              <span className="font-semibold text-brand-navy">
                innovation, operational excellence, and sustainable growth
              </span>
              .
            </p>

            {/* Expertise pills */}
            <div className="mt-10">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-semibold mb-4">
                Process Expertise
              </div>
              <div className="flex flex-wrap gap-2">
                {EXPERTISE.map((e) => (
                  <span
                    key={e}
                    className="text-xs font-semibold text-brand-navy bg-brand-soft border border-gray-200 px-3 py-1.5"
                  >
                    {e}
                  </span>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div className="mt-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-semibold mb-4">
                Certifications &amp; Standards
              </div>
              <div className="grid grid-cols-2 gap-3">
                {CERTIFICATIONS.map((c) => (
                  <div key={c} className="flex items-center gap-2.5 text-sm text-brand-text/80">
                    <CheckCircle2 className="h-4 w-4 text-brand-green shrink-0" />
                    {c}
                  </div>
                ))}
              </div>
            </div>

            {/* Global partners */}
            <div className="mt-10 bg-brand-soft border border-gray-200 p-6 lg:p-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-semibold mb-3">
                Long-Standing Global Partnerships
              </div>
              <p className="text-sm text-brand-text/70 mb-5">
                Successfully built long-term partnerships with global customers
                including:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CLIENTS.map((c) => (
                  <div
                    key={c}
                    className="flex items-center gap-2.5 text-sm font-semibold text-brand-navy"
                  >
                    <span className="h-1.5 w-1.5 bg-brand-orange inline-block" />
                    {c}
                  </div>
                ))}
              </div>
            </div>

            {/* Key metrics */}
            <div className="mt-10 grid grid-cols-3 gap-8">
              {[
                { k: "25+", v: "Years of Experience" },
                { k: "6+", v: "Process Domains" },
                { k: "10+", v: "Global OEM Partners" },
              ].map((m) => (
                <div key={m.v} className="border-l-2 border-brand-orange pl-4">
                  <div className="font-heading font-extrabold text-3xl text-brand-navy">
                    {m.k}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.18em] text-brand-text/60 leading-snug">
                    {m.v}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
